import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { AssetDto, AssetVersionDto, GalleryDto } from '@nusign/picflow-backend-types';
import { DownloadSizeSelectionComponent } from '../../../gallery-shared/components/download-size-selection/download-size-selection.component';
import { getAllowedDownloadSizes } from '../../../lib/convert-gallery-download-size';
import { IsIos } from '../../../lib/is-ios';
import { DialogService } from '../../../services/dialog.service';
import { DownloadAssetService } from '../../../services/download-asset.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { IosDownloadDialogComponent } from '../../dialogs/ios-download-dialog/ios-download-dialog.component';
import { showIosDownloadMenu } from '../../lib/show-ios-download-menu';

@Component({
  selector: 'app-asset-download-button',
  templateUrl: './asset-download-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ContextMenuComponent, DownloadSizeSelectionComponent, TooltipDirective, NgTemplateOutlet],
})
export class AssetDownloadButtonComponent {
  private downloadAssetService = inject(DownloadAssetService);
  private dialog = inject(DialogService);

  @HostListener('window:resize')
  onResize() {
    this.windowWidth.set(window.innerWidth);
  }
  windowWidth = signal(window.innerWidth);
  isMobile = computed(() => this.windowWidth() < 600);

  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly asset = input<AssetDto>(undefined);
  readonly version = input<AssetVersionDto>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly reportDownloads = input(true);
  readonly downloadTemplate = input(false);
  readonly onClose = output();
  readonly downloading = signal(false);

  readonly singleDownloadSize = computed(() => {
    const gallery = this.gallery();
    const asset = this.asset();
    const galleryPermissions = this.galleryPermissions();
    if (!gallery || !asset || !galleryPermissions) return;

    const { sizes, allowOriginalDownload } = getAllowedDownloadSizes(
      gallery.download_media_size,
      galleryPermissions,
      asset
    );

    const allowedSizesCount = sizes.filter(s => s.name !== 'original').length + (allowOriginalDownload ? 1 : 0);
    if (allowedSizesCount !== 1) return;
    return sizes[0]?.size || 0;
  });

  readonly iosDownloadMode = computed(() => this.isMobile() && IsIos());

  download(longEdge: number) {
    if (showIosDownloadMenu(this.gallery(), this.galleryPermissions(), this.asset(), this.version())) {
      this.onClose.emit();
      return this.dialog.open(IosDownloadDialogComponent, {
        assetId: this.asset().id,
        versionId: this.version()?.id || (this.asset().version as string),
        galleryId: this.gallery().id,
      });
    }
    this.downloadAssetService
      .autoDownloadAsset(
        this.asset(),
        this.version() || this.asset().version_data,
        this.gallery(),
        longEdge,
        this.reportDownloads()
      )
      .subscribe(res => {
        this.downloading.set(res === 'downloading');
      });
  }
}
